import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import * as ActionTypes from '../actions/types';
import GENERAL_FUNCTIONS from '../utils/functions/functions';
import {
    changeClaimApplicationStateAction,
    createWithdrawnClaimApplicationSuccessAction,
    fetchClaimApplicationBankAccountAnswersSuccessAction,
    fetchClaimApplicationByIdSuccessAction,
    fetchClaimApplicationsFailedAction,
    fetchClaimApplicationsSuccessAction,
    fetchClaimApplicationTemplateSuccessAction,
} from '../actions/claimApplication';

interface ClaimApplicationType {
    id: string;
    code?: string;
    description?: string;
    name?: string;
    ClaimApplicationState?: string;
}

type State = {
    claimApplication:
        | {
              byId:
                  | {
                        [id: string]: ClaimApplicationType | any;
                    }
                  | {};
              allIds: Array<string> | any;
          }
        | any;
    bankDetails: any;
    loading: boolean;
    loadingBankAccountAnswers: boolean;
    errorMessage: null | string | Array<string>;
    pagination: null | any;
    submitting: boolean;
    isDraftApplicationRequestFinished: boolean;
    claimApplicationTemplate: {
        byId:
            | {
                  [id: string]: ClaimApplicationType | any;
              }
            | {};
        allIds?: any[] | any;
    };
};

interface Template extends State {
    bankDetails: any;
    submitting: boolean;
    pagination: any;
    loadingBankAccountAnswers: boolean;
    claimApplication: any;
    errorMessage: string | Array<string> | null;
    loading: boolean;
    claimApplicationTemplate: { byId: { [p: string]: any } | {}; allIds?: any };
    isDraftApplicationRequestFinished: boolean;
    allIds: any[];
}

const INITIAL_STATE: State = {
    claimApplication: {
        byId: {},
        allIds: [],
    },
    bankDetails: [],
    loading: false,
    loadingBankAccountAnswers: false,
    errorMessage: null,
    pagination: null,
    submitting: false,
    isDraftApplicationRequestFinished: true,
    claimApplicationTemplate: {
        byId: {},
        allIds: [],
    },
};

const sendClaimApplication = (state: State): State => {
    return {
        ...state,
        submitting: true,
        errorMessage: null,
    };
};

const sendClaimApplicationSuccess = (state: State): State => {
    return {
        ...state,
        submitting: false,
        errorMessage: null,
    };
};

const sendClaimApplicationFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
        errorMessage: null,
    };
};

const updateClaimApplication = (state: State): State => {
    return {
        ...state,
        submitting: true,
        errorMessage: null,
    };
};

const updateClaimApplicationSuccess = (state: State): State => {
    return {
        ...state,
        submitting: false,
        errorMessage: null,
    };
};

const updateClaimApplicationFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
        errorMessage: null,
    };
};

const fetchClaimApplicationById = (state: State): State => {
    return {
        ...state,
        loading: true,
        errorMessage: null,
    };
};

const fetchClaimApplicationByIdSuccess = (
    state: State,
    action: PayloadAction<fetchClaimApplicationByIdSuccessAction>,
): State => {
    const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById('id', [
        action.payload.body.data,
    ]);

    return {
        ...state,
        loading: false,
        errorMessage: null,
        claimApplication: {
            byId,
            allIds,
        },
    };
};

const fetchClaimApplicationByIdFailed = (state: State): State => {
    return {
        ...state,
        loading: false,
        errorMessage: null,
    };
};

const fetchClaimApplications = (state: State): State => {
    return {
        ...state,
        loading: true,
        errorMessage: null,
    };
};

const fetchClaimApplicationsSuccess = (
    state: State,
    action: PayloadAction<fetchClaimApplicationsSuccessAction>,
): State => {
    const { byId, allIds } = GENERAL_FUNCTIONS.normalizeById(
        'id',
        action.payload.body.data,
    );

    return {
        ...state,
        loading: false,
        errorMessage: null,
        claimApplication: {
            byId,
            allIds,
        },
        pagination: {
            page: action.payload.body.page,
            limit: action.payload.body.limit,
            totalPages: action.payload.body.totalPages,
            count: action.payload.body.count,
            totalItems: action.payload.body.totalCount,
        },
    };
};

const fetchClaimApplicationsFailed = (
    state: State,
    action: PayloadAction<fetchClaimApplicationsFailedAction>,
): State => {
    const errorMessage =
        action.payload &&
        action.payload.response &&
        action.payload.response.body &&
        action.payload.response.body.errors &&
        action.payload.response.body.errors.length > 0
            ? action.payload.response.body.errors.map((error: string) => error)
            : null;
    return {
        ...state,
        loading: false,
        errorMessage,
    };
};

const changeClaimApplicationState = (
    state: State,
    action: PayloadAction<changeClaimApplicationStateAction>,
): State => {
    return {
        ...state,
        [action.payload.key]: action.payload.value,
    };
};

const sendDraftClaimApplicationByUser = (state: State): State => {
    return {
        ...state,
        loading: true,
        isDraftApplicationRequestFinished: false,
    };
};

const sendDraftClaimApplicationByUserSuccess = (state: State): State => {
    return {
        ...state,
        loading: false,
        isDraftApplicationRequestFinished: true,
    };
};

const sendDraftClaimApplicationByUserFailed = (state: State): State => {
    return {
        ...state,
        loading: false,
        isDraftApplicationRequestFinished: true,
    };
};

const createClaimApplicationTransition = (state: State) => {
    return {
        ...state,
        submitting: true,
    };
};

const createClaimApplicationTransitionSuccess = (state: State) => {
    return {
        ...state,
        submitting: false,
    };
};

const createClaimApplicationTransitionFailed = (state: State) => {
    return {
        ...state,
        submitting: false,
    };
};

const fetchClaimApplicationBankAccountAnswers = (state: State): State => {
    return {
        ...state,
        loadingBankAccountAnswers: true,
        bankDetails: [],
    };
};

const fetchClaimApplicationBankAccountAnswersSuccess = (
    state: State,
    action: PayloadAction<fetchClaimApplicationBankAccountAnswersSuccessAction>,
): State => {
    return {
        ...state,
        loadingBankAccountAnswers: false,
        bankDetails: action.payload?.body?.data,
    };
};

const fetchClaimApplicationBankAccountAnswersFailed = (state: State): State => {
    return {
        ...state,
        loadingBankAccountAnswers: false,
    };
};

const updateClaimApplicationTransition = (state: State): State => {
    return {
        ...state,
        submitting: true,
    };
};

const updateClaimApplicationTransitionSuccess = (state: State): State => {
    return {
        ...state,
        submitting: false,
    };
};

const updateClaimApplicationTransitionFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
    };
};

const createWithdrawnClaimApplication = (state: State): State => {
    return {
        ...state,
        submitting: true,
    };
};

const createWithdrawnClaimApplicationSuccess = (
    state: State,
    action: PayloadAction<createWithdrawnClaimApplicationSuccessAction>,
): State => {
    let claimApplicationId: any = action.payload.body.data;

    return {
        ...state,
        submitting: false,

        claimApplication: {
            ...state.claimApplication,
            byId: {
                ...state.claimApplication.byId,
                [claimApplicationId]: {
                    ...state.claimApplication.byId[claimApplicationId],
                    withdrawn: true,
                },
            },
            allIds: state.claimApplication.allIds,
        },
    };
};

const createWithdrawnClaimApplicationFailed = (state: State): State => {
    return {
        ...state,
        submitting: false,
    };
};

const fetchClaimApplicationTemplate = (state: State): State => {
    return {
        ...state,
        loading: true,
    };
};

const fetchClaimApplicationTemplateSuccess = (
    state: State,
    action: PayloadAction<fetchClaimApplicationTemplateSuccessAction>,
): Template => {
    let claimApplicationId = action.payload.body.data;

    if (!claimApplicationId) {
        return {
            ...state,
            loading: false,

            claimApplicationTemplate: {
                ...state.claimApplicationTemplate,
            },
            allIds: [...state.claimApplicationTemplate.allIds],
        };
    }

    return {
        allIds: [],
        ...state,
        loading: false,
        claimApplicationTemplate: {
            ...state.claimApplicationTemplate,
            byId: {
                ...state.claimApplicationTemplate.byId,
                [claimApplicationId.claimApplicationId]: {
                    ...state.claimApplication.byId[
                        claimApplicationId.claimApplicationId
                    ],
                    content: claimApplicationId.content,
                },
            },
            allIds: [
                ...state.claimApplicationTemplate.allIds,
                claimApplicationId.claimApplicationId,
            ],
        },
    };
};
const fetchClaimApplicationTemplateFailed = (state: State): State => {
    return {
        ...state,
        loading: false,
    };
};

const claimApplication = createReducer(INITIAL_STATE, {
    [ActionTypes.SEND_CLAIM_APPLICATION]: sendClaimApplication,
    [ActionTypes.SEND_CLAIM_APPLICATION_SUCCESS]: sendClaimApplicationSuccess,
    [ActionTypes.SEND_CLAIM_APPLICATION_FAILED]: sendClaimApplicationFailed,
    [ActionTypes.UPDATE_CLAIM_APPLICATION]: updateClaimApplication,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_SUCCESS]:
        updateClaimApplicationSuccess,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_FAILED]: updateClaimApplicationFailed,
    [ActionTypes.GET_CLAIM_APPLICATION_BY_ID]: fetchClaimApplicationById,
    [ActionTypes.GET_CLAIM_APPLICATION_BY_ID_SUCCESS]:
        fetchClaimApplicationByIdSuccess,
    [ActionTypes.GET_CLAIM_APPLICATION_BY_ID_FAILED]:
        fetchClaimApplicationByIdFailed,
    [ActionTypes.GET_CLAIM_APPLICATIONS]: fetchClaimApplications,
    [ActionTypes.GET_CLAIM_APPLICATIONS_SUCCESS]: fetchClaimApplicationsSuccess,
    [ActionTypes.GET_CLAIM_APPLICATIONS_FAILED]: fetchClaimApplicationsFailed,
    [ActionTypes.CHANGE_CLAIM_APPLICATION_STATE]: changeClaimApplicationState,
    [ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER]:
        sendDraftClaimApplicationByUser,
    [ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_SUCCESS]:
        sendDraftClaimApplicationByUserSuccess,
    [ActionTypes.SEND_DRAFT_CLAIM_APPLICATION_BY_USER_FAILED]:
        sendDraftClaimApplicationByUserFailed,
    [ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS]:
        fetchClaimApplicationBankAccountAnswers,
    [ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_SUCCESS]:
        fetchClaimApplicationBankAccountAnswersSuccess,
    [ActionTypes.FETCH_CLAIM_APPLICATION_BANK_ACCOUNT_ANSWERS_FAILED]:
        fetchClaimApplicationBankAccountAnswersFailed,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT]:
        updateClaimApplicationTransition,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_SUCCESS]:
        updateClaimApplicationTransitionSuccess,
    [ActionTypes.UPDATE_CLAIM_APPLICATION_BANK_ACCOUNT_FAILED]:
        updateClaimApplicationTransitionFailed,
    [ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION]:
        createWithdrawnClaimApplication,
    [ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_SUCCESS]:
        createWithdrawnClaimApplicationSuccess,
    [ActionTypes.CREATE_WITHDRAWN_CLAIM_APPLICATION_FAILED]:
        createWithdrawnClaimApplicationFailed,
    [ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE]:
        fetchClaimApplicationTemplate,
    [ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_SUCCESS]:
        fetchClaimApplicationTemplateSuccess,
    [ActionTypes.FETCH_CLAIM_APPLICATION_TEMPLATE_FAILED]:
        fetchClaimApplicationTemplateFailed,
});

export default claimApplication;
